import React, { Component } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { appRef } from '../../../app'
import { HiSwitchVertical, HiHashtag, HiChip, HiOutlineMenu } from 'react-icons/hi'
import classnames from 'classnames'
import { onCustomerConnect, clearCustomerConnect, IListenerUID, CustomerState } from '../../../wsclient/handlers/customertraffic.handler'
import { IClientData, getClientById } from '../../../api/clients'
import { toast } from 'react-toastify'
import { getString } from '../../../locale'
import { ChipModel } from '../../../constants/chip-model.enum'

import { getMachine, IMachine } from '../../../api/0x0001/machine'

import { getClientStaticTraits, IStaticTraits } from '../../../api/0x0001/statictraits'
import { isSoftwareOutdated } from '../../../api/0x0001/software-versions'
import SystemInfoTab from './systeminfo.tab'
import LogsTab from './logs.tab'
import SettingsTab from './settings.tab'
import WifiConfigTab from './wificonfig.tab'
import DateTimeTab from './datetime.tab'
import CustomerDataTab from './customerdata.tab'
import SystemActionsTab from './systemactions.tab'
import MonitoringTab from './monitoring.tab'
import OtaTab from './ota.tab'
import FlashDeviceTab from './flashdevice.tab'


type Props = {
    client: IClientData
}

type State = {
    showMobileMenu: boolean
    tab: Tab
    staticTraits?: IStaticTraits
    outdated: boolean
    newVersion?: string
}

enum Tab {
    SystemInfo,
    Logs,
    Settings,
    WifiConfig,
    DateTime,
    CustomerData,
    SystemActions,
    Monitoring,
    Ota,
    FlashDevice
}

const TabSlug: { [key in Tab]: string } = {
    [Tab.SystemInfo]: 'system-info',
    [Tab.Logs]: 'logs',
    [Tab.Settings]: 'settings',
    [Tab.WifiConfig]: 'wifi-config',
    [Tab.DateTime]: 'datetime',
    [Tab.CustomerData]: 'customer-data',
    [Tab.SystemActions]: 'system-actions',
    [Tab.Monitoring]: 'monitoring',
    [Tab.Ota]: 'ota',
    [Tab.FlashDevice]: 'flash-device',
}

const SlugToTab: { [key: string]: Tab } = {
    'system-info': Tab.SystemInfo,
    'logs': Tab.Logs,
    'settings': Tab.Settings,
    'wifi-config': Tab.WifiConfig,
    'datetime': Tab.DateTime,
    'customer-data': Tab.CustomerData,
    'system-actions': Tab.SystemActions,
    'monitoring': Tab.Monitoring,
    'ota': Tab.Ota,
    'flash-device': Tab.FlashDevice,
}

export class Client extends Component<Props & RouteComponentProps> {
    state: State = {
        showMobileMenu: false,
        tab: process.env.NODE_ENV === 'development' ? Tab.SystemInfo : Tab.SystemInfo,
        outdated: false,
        newVersion: undefined,
    }

    machine?: IMachine

    listenerId?: IListenerUID

    componentDidMount = async () => {
        console.log('Loading 0x0001 Model Data')

        const { client } = this.props

        const urlParams = new URLSearchParams(this.props.location.search)
        const tabSlug = urlParams.get('tab')
        if (tabSlug && SlugToTab[tabSlug] !== undefined) {
            this.setState({ tab: SlugToTab[tabSlug] })
        }

        const [clientResult, machineResult, staticTraitsResult] = await Promise.allSettled([
            getClientById(client.id),
            getMachine(client.id),
            getClientStaticTraits(client.id),
        ])

        if (clientResult.status === 'fulfilled') {
            this.setState({ client: clientResult.value.data })
        } else {
            console.error('Failed to get client data', clientResult.reason)
        }

        if (machineResult.status === 'fulfilled') {
            this.machine = machineResult.value.data
        } else {
            console.error('Failed to get machine data', machineResult.reason)
        }

        if (staticTraitsResult.status === 'fulfilled') {
            const staticTraits = staticTraitsResult.value.data
            console.log('staticTraits', staticTraits)
            this.setState({ staticTraits })

            try {
                const versionCheck = await isSoftwareOutdated(staticTraits.version, staticTraits.model)
                this.setState({
                    outdated: versionCheck.data.outdated,
                    newVersion: versionCheck.data.softwareVersion?.version,
                })
            } catch (e) {
                toast.error('Failed to check software version', e)
            }
        } else {
            console.error('Failed to get static traits', staticTraitsResult.reason)
        }

        appRef.setHeader(
            <HiSwitchVertical className="me-2" size={28} />,
            () => `${this.machine?.name || getString('pagelabels.client')} ${client.id}`,
        )
        this.listenerId = onCustomerConnect(this.handleClientConnect.bind(this))
    }

    componentWillUnmount = () => {
        clearCustomerConnect(this.listenerId!)
    }

    handleClientConnect(clientConnect: IClientData, state: CustomerState) {
        // handled globally in App to keep disconnect behavior consistent
    }

    render() {
        return (
            <div className="flex flex-col lg:grid grid-cols-3 gap-5 lg:gap-10">
                <div>{this.renderSidebar()}</div>
                <div className="col-span-2">{this.renderContent()}</div>
            </div>
        )
    }

    renderSidebar() {
        return (
            <>
                <div className="hidden lg:block sticky top-5">
                    {this.renderSideNav()}
                    {this.renderStaticTraits()}
                </div>
                <div className="w-full flex lg:hidden justify-end">
                    <button
                        title="menu"
                        className="text-white hover:text-gray-300 focus:outline-none focus:text-gray-300"
                        onClick={() => {
                            this.setState({ showMobileMenu: !this.state.showMobileMenu })
                        }}
                    >
                        <HiOutlineMenu className="h-6 w-6" />
                    </button>
                </div>
                {this.state.showMobileMenu && (
                    <div className="mt-5">
                        {this.renderSideNav()}
                        {this.renderStaticTraits()}
                    </div>
                )}
            </>
        )
    }

    renderStaticTraits() {
        return (
            <ul className="bg-white dark:bg-gray-800 rounded-xl shadow-md">
                {/* {this.renderRow(
                    getString('static_traits.version'),
                    staticTraits?.version,
                    false,
                    outdated && newVersion && (
                        <span className="text-yellow-500 text-sm">({getString('common.old_software_message', newVersion)})</span>
                    ),
                )}
                {this.renderRow(getString('static_traits.idf_ver'), staticTraits?.idfVer)}
                {this.renderRow(getString('static_traits.cores'), staticTraits?.cores)}
                {this.renderRow(getString('static_traits.model'), staticTraits?.model ? ChipModel[staticTraits.model] : undefined)}
                {this.renderRow(getString('static_traits.revision'), staticTraits?.revision)} */}

                {this.renderStaticTrait(getString('static_traits.version'), HiChip, this.state.staticTraits?.version)}
                {this.renderStaticTrait(getString('static_traits.idf_ver'), HiChip, this.state.staticTraits?.idfVer)}
                {this.renderStaticTrait(getString('static_traits.cores'), HiChip, this.state.staticTraits?.cores)}
                {this.renderStaticTrait(
                    getString('static_traits.model'),
                    HiChip,
                    this.state.staticTraits?.model ? ChipModel[this.state.staticTraits.model] : undefined,
                )}
                {this.renderStaticTrait(getString('static_traits.revision'), HiChip, this.state.staticTraits?.revision)}
            </ul>
        )
    }

    renderStaticTrait(label: string, IconComp: React.ElementType, value?: string | number | JSX.Element) {
        return (
            <li className="flex items-center p-4 dark:text-white first:rounded-t-xl last:rounded-b-xl text-sm">
                <div className="flex items-center">
                    <IconComp className="me-5" />
                    {label}
                </div>
                <div className="flex-grow" />
                <div>{value}</div>
            </li>
        )
    }

    renderSideNav() {
        const { client } = this.props
        return (
            <ul className="bg-white dark:bg-gray-800 rounded-xl shadow-md mb-5">
                {this.renderSidebarItem(Tab.SystemInfo, getString('client.system_info'))}
                {this.renderSidebarItem(Tab.Logs, getString('client.logs'))}
                {this.renderSidebarItem(Tab.Settings, getString('client.settings'))}
                {this.renderSidebarItem(Tab.WifiConfig, getString('client.wifi_config'))}
                {this.renderSidebarItem(Tab.DateTime, getString('client.datetime'))}
                {this.renderSidebarItem(Tab.CustomerData, getString('client.customer_data'))}
                {this.renderSidebarItem(Tab.SystemActions, getString('client.system_actions'))}
                {this.renderSidebarItem(Tab.Monitoring, getString('client.monitoring'))}
                {this.renderSidebarItem(Tab.Ota, getString('client.ota'))}
                {client?.phyDebugger && this.renderSidebarItem(Tab.FlashDevice, getString('client.flash_device'))}
            </ul>
        )
    }

    renderSidebarItem(tabId: number, label: string) {
        const { tab } = this.state
        return (
            <li
                className={classnames(
                    tab === tabId && 'bg-gray-200 dark:bg-gray-700',
                    'flex items-center p-4 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors first:rounded-t-xl last:rounded-b-xl cursor-pointer',
                )}
                onClick={() => {
                    this.setState({ tab: tabId, showMobileMenu: false })
                    const tabSlug = TabSlug[tabId as Tab]
                    const newUrl = `${this.props.location.pathname}?tab=${tabSlug}`
                    this.props.history.replace(newUrl)
                }}
            >
                <HiHashtag className="me-5" />
                {label}
            </li>
        )
    }

    renderContent() {
        const { client } = this.props
        const { tab } = this.state
        if (!client) return undefined

        switch (tab) {
            case Tab.SystemInfo:
                return <SystemInfoTab clientId={client.id} />
            case Tab.Logs:
                return <LogsTab clientId={client.id} />
            case Tab.Settings:
                return <SettingsTab clientId={client.id} />
            case Tab.WifiConfig:
                return <WifiConfigTab clientId={client.id} />
            case Tab.DateTime:
                return <DateTimeTab clientId={client.id} />
            case Tab.CustomerData:
                return <CustomerDataTab clientId={client.id} />
            case Tab.SystemActions:
                return <SystemActionsTab clientId={client.id} />
            case Tab.Monitoring:
                return <MonitoringTab clientId={client.id} />
            case Tab.Ota:
                return <OtaTab clientId={client.id} />
            case Tab.FlashDevice:
                return <FlashDeviceTab clientId={client.id} />
        }
    }
}
